<template>
    <div>
        <m-form class="py-0 m-form-user m-form-user__form" ref="form" v-if="organizationIdentifierName">
            <template #content-template>
                <div class="content-border-area">
                    <m-block-form-fields :required="false">
                        <template #title>
                            {{ $t('modules.forms.m-form-user.sections.personal.title') }}
                        </template>

                        <template>
                            <v-layout wrap>
                                <v-flex xs12>
                                    <e-input-user-name required-style
                                                       rules="required"
                                                       tabindex="0"
                                                       v-model="form.firstName"
                                                       @blur="setUserId"/>
                                </v-flex>
                                <v-flex xs12>
                                    <e-input-user-surname required-style
                                                          rules="required"
                                                          v-model="form.lastName"
                                                          @blur="setUserId"/>
                                </v-flex>
                                <v-flex xs12>
                                    <e-input-user-id
                                            v-model="form.username"
                                            :organizationId="organizationIdentifierName"
                                            :loading="loadingIdentifier"
                                            @checkUserInput="checkUserId"
                                            ref="userId"
                                            :permanentlyDisabled="disableUserIdentifier"
                                            :tooltip="breakpoints.isLessThanTablet ? null : userIdTooltipText"
                                    />
                                </v-flex>
                                <v-flex xs12>
                                    <e-input-user-position-function v-model="form.position"/>
                                </v-flex>
                            </v-layout>
                        </template>
                    </m-block-form-fields>

                    <m-block-form-fields>
                        <template #title>
                            {{ $t('modules.forms.m-form-user.sections.contacts.title') }}
                        </template>

                        <template>
                            <m-block-form-fields :enabled="isCreationForm || (!isCreationForm && !!editingUser.email)"
                                                 :offset="false"
                                                 child
                                                 ref="emailBlock">

                                <template #switch-label="">{{ $t('modules.blocks.m-block-form-fields-email.title') }}
                                </template>

                                <template>
                                    <v-layout wrap>
                                        <v-flex xs12>
                                            <e-input-user-email :label="labelEmail"
                                                                name="email"
                                                                ref="email"
                                                                rules="email"
                                                                v-model="form.email"/>
                                        </v-flex>
                                    </v-layout>
                                </template>
                            </m-block-form-fields>

                            <m-block-form-fields :enabled="!isCreationForm && !!editingUser.mobile"
                                                 :offset="false"
                                                 child
                                                 ref="mobileBlock">

                                <template #switch-label="">{{ $t('modules.blocks.m-block-form-fields-phone.title') }}
                                </template>

                                <template>
                                    <v-layout wrap>
                                        <v-flex xs12>
                                            <e-input-user-mobile-phone :label="labelMobile"
                                                                       name="mobile"
                                                                       ref="mobile"
                                                                       rules="mobile"
                                                                       v-model="form.mobile"/>
                                        </v-flex>
                                    </v-layout>
                                </template>
                            </m-block-form-fields>
                        </template>
                    </m-block-form-fields>

                    <m-block-form-fields child>
                        <template #title>
                            {{ $t('modules.forms.m-form-user.sections.role.title') }}
                            <m-tooltip-info>
                                <e-typography-heading-3>{{ $t(`elements.inputs.m-form-user.tooltip.heading.guest`) }}
                                </e-typography-heading-3>
                                <v-layout class="pb-4">
                                    {{ $t(`elements.inputs.m-form-user.tooltip.guest`) }}
                                </v-layout>
                                <e-typography-heading-3>{{ $t(`elements.inputs.m-form-user.tooltip.heading.employee`) }}
                                </e-typography-heading-3>
                                <v-layout class="pb-4">
                                    {{ $t(`elements.inputs.m-form-user.tooltip.employee`) }}
                                </v-layout>
                                <e-typography-heading-3>{{ $t(`elements.inputs.m-form-user.tooltip.heading.admin`) }}
                                </e-typography-heading-3>
                                <v-layout class="pb-4">
                                    {{ $t(`elements.inputs.m-form-user.tooltip.admin`) }}
                                </v-layout>


                            </m-tooltip-info>
                        </template>

                        <template>
                            <e-input-select-user-role required-style :context="$actions.create"
                                                      :default-model="!isCreationForm"
                                                      v-model="form.roles"/>
                        </template>
                    </m-block-form-fields>
                </div>
            </template>

            <template #buttons>
                <m-block-form-buttons :isLoading="$wait.waiting([keys.users.create,
                                            `${keys.users.update}${$route.params.id}`])"
                                      :text="text"
                                      @cancel="cancel"
                                      @submit="submit"
                                      v-if="!isOnboarding"/>
            </template>
        </m-form>

        <div class="create-new-organisation-identifier" v-else>
            <v-layout wrap>
                <v-flex xs-12>
                    <e-typography-body>
                        Sie benötigen eine Organisationkennung, um neue Mitarbeiter anzulegen.
                    </e-typography-body>
                </v-flex>
            </v-layout>
            <v-layout>
                <v-flex>
                    <e-button-primary :to="nav.dashboard.account.createIdentifier.path()" class="create-new-organisation-identifier__button">
                        Organisationskennung jetzt anlegen
                    </e-button-primary>
                </v-flex>
            </v-layout>
        </div>
    </div>
</template>

<script>
    import baseForm from "@mixins/components/modules/forms/base";

    import {mapActions, mapGetters} from 'vuex';
    import {setRelations} from "@utils/jsonApi";

    import passwordGenerator from "@utils/passwordGenerator";
    import MTooltipInfo from "../../tooltips/concrete/m-tooltip-info";
    import ETypographyHeading3 from "@elements/typography/e-typography-heading-3";
    import EInputUserId from "@elements/inputs/concrete/user/e-input-user-id";
    import ETypographyBody from "../../../elements/typography/e-typography-body";
    import EButtonPrimary from "../../../elements/buttons/theme/e-button-primary";
    import letterReplacement from "@utils/letterReplacement";

    let name = 'm-form-user';
    export default {
        name: name,
        components: {EButtonPrimary, ETypographyBody, ETypographyHeading3, MTooltipInfo, EInputUserId},
        mixins: [baseForm],
        props: {
            isOnboarding: {
                type: Boolean,
                default: false
            },
            action: {
                type: String,
                default: ''
            },
            name: {
                type: String,
                default: name
            },
            title: {
                type: String,
                default: ''
            },
            type: {
                type: String,
                default: 'create',
                validator: function (value) {
                    return ['edit', 'create'].indexOf(value) !== -1
                }
            },
        },
        data: () => ({
            form: {
                firstName: '',
                lastName: '',
                username: '',
                position: '',
                email: '',
                mobile: '',
                roles: [],
            },
            loadingIdentifier: false
        }),
        async created() {
            if (!this.organization.length) {
                await this.requestOrganization({id: this.organizationId});
            }
            if (!this.isCreationForm && this.$route.params.id) {
                if (!this.editingUser.length) {
                    await this.requestUser({id: this.$route.params.id});
                }
                Object.assign(this.form, this.editingUser);
                this.form.roles.pop()
            }
        },
        computed: {
            ...mapGetters({
                getUser: "users/read",
                getOrganization: 'organizations/read'
            }),
            organizationId() {
                return this.$user.relationships.organization.data.id;
            },
            organization() {
                return this.getOrganization(this.organizationId)
            },
            organizationIdentifierName() {
                return this.organization.identifierName
            },
            userIdTooltipText() {
                return this.$t('modules.forms.m-form-company-reg-step4.inputs.user-id.tooltip')
            },
            disableUserIdentifier() {
                return this.type == 'edit'
            },
            text() {
                return {
                    cancel: this.$t('modules.forms.m-form-user.buttons.cancel'),
                    submit: this.$t('modules.forms.m-form-user.buttons.submit')
                }
            },
            editingUser() {
                return this.getUser(this.$route.params.id)
            },
            isCreationForm() {
                return this.type === 'create'
            },
            labelMobile() {
                return this.$t('elements.inputs.e-input-users-phone-mobile.label') + ' *'
            },
            labelEmail() {
                return this.$t('elements.inputs.e-input-users-email.label') + ' *'
            }
        },
        methods: {
            ...mapActions({
                requestCreateUser: 'users/create',
                requestUpdateUser: 'users/update',
                requestUser: 'users/read',
                identify: 'users/identifier',
                requestOrganization: 'organizations/read',
            }),
            async checkUserId() {
                if (!this.loadingIdentifier && this.form.username) {
                    this.loadingIdentifier = true;
                    let body = {
                        data: {
                            firstName: this.form.firstName,
                            lastName: this.form.lastName,
                            userName: this.form.username,
                            organizationIdentifierName: this.organizationIdentifierName
                        }
                    }
                    try {
                        let res = await this.identify(body)
                        if (res.data.userName) this.form.username = res.data.userName
                    } catch (e) {
                        if (e.response) {
                            this.$refs.userId.invalid(e.response.data.details)
                        }
                    } finally {
                        this.loadingIdentifier = false;
                    }
                }
            },
            async setUserId() {
                if (!this.disableUserIdentifier && !this.loadingIdentifier && this.form.firstName !== '' && this.form.lastName !== '') {
                    this.loadingIdentifier = true;
                    let body = {
                        data: {
                            firstName: letterReplacement.replaceLetters(this.form.firstName),
                            lastName: letterReplacement.replaceLetters(this.form.lastName),
                            organizationIdentifierName: this.organizationIdentifierName
                        }
                    }
                    let res = await this.identify(body)
                    this.form.username = res.data.userName;
                    this.loadingIdentifier = false;
                }

            },
            async submit() {
                const validAll = await this.$validator.validateAll();
                const validIdentify = this.validateIdentify();
                if (!validAll || !validIdentify) {
                    const el = document.querySelector(".v-messages__message");
                    el.scrollIntoView({behavior: "smooth", block: "center"})
                    this.notify.error(this.$t('notifications.errors.formErrors.invalid'))
                    return;
                }
                try {
                    this.setOrganizationRelation();
                    let user = this.isCreationForm ? await this.createUser() : await this.updateUser();
                    this.notify.success(this.$t(`notifications.account.userManagement.${this.isCreationForm ? 'created' : 'edited'}`, {
                        firstName: user.firstName,
                        lastName: user.lastName
                    }))
                    if (this.isOnboarding) {
                        this.$emit('next')
                    } else {
                        this.redirect(this.nav.dashboard.account.users.view.name, {id: user._id})
                    }
                } catch (e) {
                    if (e.response && e.response.status === 400) {
                        e.response.data.violations.forEach(violation => {
                            if (violation.propertyPath === 'username') return; //TODO temporary workaround, remove it when we get correct response from server
                            this.$validator.errors.add({
                                id: violation.propertyPath,
                                field: violation.propertyPath,
                                msg: violation.message
                            });

                            const field = this.$validator.fields.find({name: violation.propertyPath});

                            if (!field) {
                                throw `Field "${violation.propertyPath}" in "${this.$options._componentTag}" component don't have validation on client side!`;
                            }

                            field.setFlags({
                                invalid: true,
                                valid: false,
                                validated: true
                            });
                        });
                    } else if (e.response && e.response.status === 403) {
                        this.notify.error(this.$t('notifications.errors.formErrors.emailAlreadyUsed'))
                    } else if (e.response && e.response.status === 500) {
                        this.notify.error(this.$t('notifications.errors.formErrors.userIdInvalid'))
                    } else {
                        this.notify.processUnhandledError(e);
                    }
                }
            },
            async createUser() {
                return await this.requestCreateUser({
                    ...this.form,
                    username: this.form.username,
                    password: passwordGenerator.generate(),
                    needToChangePassword: true
                });
            },
            async updateUser() {
                return await this.requestUpdateUser(this.form)
            },
            cancel() {
                this.goBack();
            },
            validateIdentify() {
                if (!this.form.email && !this.form.mobile) {
                    const fields = (({email, mobile}) => ({email, mobile}))(this.$refs);
                    Object.keys(fields).forEach((key) => {
                        let field = this.$validator.fields.find({
                            name: fields[key].name
                        });

                        this.$validator.errors.add({
                            id: field.id,
                            field: field.name,
                            msg: this.$t('modules.forms.m-form-user.sections.contacts.emptyContacts')
                        });

                        field.setFlags({
                            invalid: true,
                            valid: false,
                            validated: true
                        });
                    });
                    return false;
                }
                return true;
            },
            setOrganizationRelation() {
                const rel = {
                    organization: this.$user.relationships.organization
                };
                setRelations(this.form, rel)
            },
            capitalize(string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            },
        },
        watch: {
            action(val) {
                if (val == 'submit') {
                    this.submit()
                    this.$emit('resetAction')
                }
            }
        }
    }
</script>

<style lang="scss">
    .m-form-user {
        .content-border-area {
            padding-top: 1rem !important;
            padding-left: 2rem !important;

            @include media(md) {
                padding-top: 0rem !important;
                padding-left: 1rem !important;
            }
        }

        .fullscreen {
            .m-form-content-template {
                margin-top: 1rem;
                padding-left: 2rem;
                margin-bottom: 0;
            }

            .content-border-area {
                padding-top: 1rem !important;
                padding-left: 0rem !important;
            }
        }
    }

    .create-new-organisation-identifier{
        &__button {
            margin-left: -.5rem;
        }
    }
</style>
